import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AppTheme, RFP, RFPStatus } from '../types';
import { listRFPs, createRFP, updateRFP, deleteRFP } from '../services/rfpService';
import { getTenantConfigCached } from '../services/tenantConfigService';
import { createProject } from '../services/projectService';
import { isBeaconInstalled, searchBeaconDeals, createBeaconDeal, markBeaconDealWon, BeaconDeal } from '../services/beaconClient';
import { logActivity, ActivityEntityType } from '../services/activityService';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { ApprovalEntityType } from '../services/approvalService';
import EntityApprovalSection from '../components/approvals/EntityApprovalSection';
import PeoplePicker, { Person } from '../components/PeoplePicker';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton,
  BM_LABEL, BM_INPUT, BM_TITLE_INPUT,
} from '../components/BoardModal';
import AutoSizingTextarea from '../components/AutoSizingTextarea';

interface ViewProps { pca: IPublicClientApplication; theme: AppTheme; }

// ── Status configuration ──────────────────────────────────────────────
//
// Pipeline lifecycle uses the new optionset values 6-10 added in
// scripts/block5-rfp-schema.py. Legacy values 1-5 are still rendered
// when present on existing rows so users can see (and clean up) data
// from before the pipeline was modeled.

const PIPELINE_STATUSES: number[] = [
  RFPStatus.Identified,
  RFPStatus.GoNoBid,
  RFPStatus.InPreparation,
  RFPStatus.UnderReview,
  RFPStatus.Submitted,
  RFPStatus.Won,
  RFPStatus.Lost,
  RFPStatus.NoAward,
];

const STATUS_LABEL: Record<number, string> = {
  [RFPStatus.Draft]:         'Draft',
  [RFPStatus.Submitted]:     'Submitted',
  [RFPStatus.Won]:           'Won',
  [RFPStatus.Lost]:          'Lost',
  [RFPStatus.Archived]:      'Archived',
  [RFPStatus.Identified]:    'Identified',
  [RFPStatus.GoNoBid]:       'Go/No-Bid',
  [RFPStatus.InPreparation]: 'In Preparation',
  [RFPStatus.UnderReview]:   'Under Review',
  [RFPStatus.NoAward]:       'No Award',
};

const STATUS_BADGE: Record<number, { bg: string; fg: string }> = {
  [RFPStatus.Draft]:         { bg: '#F3F4F6', fg: '#5A6272' },
  [RFPStatus.Identified]:    { bg: '#F3F4F6', fg: '#5A6272' },
  [RFPStatus.GoNoBid]:       { bg: '#FEF3C7', fg: '#92400E' },
  [RFPStatus.InPreparation]: { bg: '#D4EEF5', fg: '#0A5568' },
  [RFPStatus.UnderReview]:   { bg: '#FEF3C7', fg: '#92400E' },
  [RFPStatus.Submitted]:     { bg: '#D4EEF5', fg: '#0A5568' },
  [RFPStatus.Won]:           { bg: '#D1FAE5', fg: '#065F46' },
  [RFPStatus.Lost]:          { bg: '#FEE2E2', fg: '#991B1B' },
  [RFPStatus.NoAward]:       { bg: '#F3F4F6', fg: '#5A6272' },
  [RFPStatus.Archived]:      { bg: '#F3F4F6', fg: '#9198A8' },
};

type FilterTab = 'all' | 'active' | 'won' | 'lost' | 'noaward';
const FILTER_TABS: { value: FilterTab; label: string; matches: (s: number | null | undefined) => boolean }[] = [
  { value: 'all',     label: 'All',      matches: () => true },
  { value: 'active',  label: 'Active',   matches: s => {
    if (s == null) return true;
    // "Active" = anything pre-resolution (not Won/Lost/NoAward/Archived).
    return s !== RFPStatus.Won && s !== RFPStatus.Lost && s !== RFPStatus.NoAward && s !== RFPStatus.Archived;
  } },
  { value: 'won',     label: 'Won',      matches: s => s === RFPStatus.Won },
  { value: 'lost',    label: 'Lost',     matches: s => s === RFPStatus.Lost },
  { value: 'noaward', label: 'No Award', matches: s => s === RFPStatus.NoAward },
];

type SortKey = 'duedate' | 'value' | 'status' | 'client' | 'name';

function pillStyle(s: number | null | undefined): React.CSSProperties {
  const v = s ?? RFPStatus.Identified;
  const c = STATUS_BADGE[v] ?? { bg: '#F3F4F6', fg: '#5A6272' };
  return {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 999,
    fontSize: 11, fontWeight: 600,
    background: c.bg, color: c.fg,
    fontFamily: 'var(--font-ui)',
  };
}

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const due = new Date(iso).getTime();
  const today = new Date().setHours(0, 0, 0, 0);
  return Math.round((due - today) / (24 * 60 * 60 * 1000));
}

// ── Main view ─────────────────────────────────────────────────────────

export default function RFPPipeline({ pca }: ViewProps) {
  const navigate = useNavigate();
  const [rfps, setRfps] = useState<RFP[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('duedate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState<RFP | null>(null);
  // The Pipeline nav tab is a manifest staticTab and always visible; the
  // admin RFP toggle governs whether the module is actually in use, so we
  // honour it at the content level (Tenant Admin can "hide" it by disabling).
  const [moduleEnabled, setModuleEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    getTenantConfigCached(pca)
      .then(c => setModuleEnabled(c.dagrun_pm_rfpenabled !== false))
      .catch(() => setModuleEnabled(true));
  }, [pca]);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setRfps(await listRFPs(pca)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
    finally { setLoading(false); }
  }, [pca]);
  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const tab = FILTER_TABS.find(t => t.value === filter)!;
    const q = search.trim().toLowerCase();
    let rows = rfps.filter(r => tab.matches(r.dagrun_pm_status));
    if (q) rows = rows.filter(r =>
      (r.dagrun_pm_name?.toLowerCase().includes(q)) ||
      (r.dagrun_pm_clientname?.toLowerCase().includes(q))
    );
    rows = [...rows].sort((a, b) => {
      let av: string | number | null | undefined;
      let bv: string | number | null | undefined;
      switch (sortKey) {
        case 'duedate':
          av = a.dagrun_pm_submissionduedate ?? a.dagrun_pm_duedate;
          bv = b.dagrun_pm_submissionduedate ?? b.dagrun_pm_duedate;
          // Null due dates sort last regardless of direction.
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          return (av < bv ? -1 : av > bv ? 1 : 0) * (sortDir === 'asc' ? 1 : -1);
        case 'value':
          av = a.dagrun_pm_estimatedvalue ?? -Infinity;
          bv = b.dagrun_pm_estimatedvalue ?? -Infinity;
          return ((av as number) - (bv as number)) * (sortDir === 'asc' ? 1 : -1);
        case 'status':
          return ((a.dagrun_pm_status ?? 0) - (b.dagrun_pm_status ?? 0)) * (sortDir === 'asc' ? 1 : -1);
        case 'client':
          return (a.dagrun_pm_clientname ?? '').localeCompare(b.dagrun_pm_clientname ?? '') * (sortDir === 'asc' ? 1 : -1);
        case 'name':
          return a.dagrun_pm_name.localeCompare(b.dagrun_pm_name) * (sortDir === 'asc' ? 1 : -1);
      }
      return 0;
    });
    return rows;
  }, [rfps, filter, search, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'value' ? 'desc' : 'asc'); }
  };
  const sortIcon = (k: SortKey) => sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  if (moduleEnabled === false) {
    return (
      <div className="page">
        <button type="button" onClick={() => navigate('/portfolio')}
          style={{ background: 'transparent', border: 'none', padding: 0, marginBottom: 12, cursor: 'pointer', fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 300, color: '#0E7490', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span aria-hidden>←</span><span>All Projects</span>
        </button>
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
          The RFP Pipeline is turned off for this tenant. A Tenant Admin can enable it in Admin → RFP Pipeline.
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Back to the projects list — mirrors the "← All Projects" breadcrumb
          in the project header so the RFP pipeline isn't a dead end. */}
      <button
        type="button"
        onClick={() => navigate('/portfolio')}
        title="Back to all projects"
        aria-label="Back to all projects"
        style={{
          background: 'transparent', border: 'none', padding: 0,
          marginBottom: 12, cursor: 'pointer',
          fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 300,
          color: '#0E7490',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          transition: 'opacity 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
        onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
      >
        <span aria-hidden>←</span><span>All Projects</span>
      </button>
      <div className="page-header" style={{ alignItems: 'flex-end', marginBottom: 16 }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 4 }}>RFP PIPELINE</div>
          <h2 style={{ margin: 0 }}>Proposals & Bids</h2>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-primary" onClick={() => setShowNew(true)}>+ New RFP</button>
        </div>
      </div>

      {/* Status filter tabs */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 12,
        borderBottom: '1px solid var(--border-light)',
      }}>
        {FILTER_TABS.map(t => {
          const count = rfps.filter(r => t.matches(r.dagrun_pm_status)).length;
          const isActive = filter === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setFilter(t.value)}
              style={{
                background: 'transparent', border: 'none',
                borderBottom: `2px solid ${isActive ? 'var(--brand)' : 'transparent'}`,
                padding: '8px 14px',
                fontFamily: 'var(--font-ui)', fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--brand)' : 'var(--ink-3)',
                cursor: 'pointer',
                transition: 'color 0.12s, border-color 0.12s',
              }}
            >
              {t.label} <span style={{
                marginLeft: 4, fontSize: 11, color: 'var(--ink-4)',
                fontFamily: 'var(--font-mono)',
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Search bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Search RFP name or client…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, maxWidth: 400,
            padding: '7px 12px',
            border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
            fontFamily: 'var(--font-ui)', fontSize: 13,
            background: 'var(--surface)', color: 'var(--ink)',
          }}
        />
        <span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
          Showing {filtered.length} of {rfps.length}
        </span>
      </div>

      {loading && <div className="spinner" style={{ margin: '60px auto' }} />}
      {error && <div className="card" style={{ padding: 16, color: 'var(--danger)', fontSize: 13 }}>{error}</div>}

      {!loading && !error && filtered.length === 0 && (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--ink-4)', fontSize: 14 }}>
          {rfps.length === 0
            ? 'No RFPs yet. Click "+ New RFP" to start tracking your first proposal.'
            : 'No RFPs match the current filter.'}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                <Th onClick={() => toggleSort('name')}>RFP{sortIcon('name')}</Th>
                <Th onClick={() => toggleSort('client')}>Client{sortIcon('client')}</Th>
                <Th onClick={() => toggleSort('value')}>Value{sortIcon('value')}</Th>
                <Th onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Th>
                <Th>Owner</Th>
                <Th onClick={() => toggleSort('duedate')}>Due{sortIcon('duedate')}</Th>
                <Th>Days</Th>
                <Th>Project</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const due = r.dagrun_pm_submissionduedate ?? r.dagrun_pm_duedate ?? null;
                const days = daysUntil(due);
                return (
                  <tr
                    key={r.dagrun_pm_rfpid}
                    onClick={() => setSelected(r)}
                    style={{
                      borderBottom: '0.5px solid var(--border-light)',
                      cursor: 'pointer', transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <Td>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{r.dagrun_pm_name}</div>
                      {r.dagrun_pm_rfpnumber && (
                        <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                          {r.dagrun_pm_rfpnumber}
                        </div>
                      )}
                    </Td>
                    <Td><span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{r.dagrun_pm_clientname ?? '—'}</span></Td>
                    <Td>
                      <span style={{ fontSize: 13, color: 'var(--ink-2)', fontFamily: 'var(--font-mono)' }}>
                        {r.dagrun_pm_estimatedvalue != null ? `$${r.dagrun_pm_estimatedvalue.toLocaleString()}` : '—'}
                      </span>
                    </Td>
                    <Td><span style={pillStyle(r.dagrun_pm_status)}>{STATUS_LABEL[r.dagrun_pm_status ?? 0] ?? '—'}</span></Td>
                    <Td><span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{r.dagrun_pm_ownername ?? '—'}</span></Td>
                    <Td>
                      <span style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
                        {due ? new Date(due).toLocaleDateString() : '—'}
                      </span>
                    </Td>
                    <Td>
                      {days == null ? <span style={{ color: 'var(--ink-4)' }}>—</span>
                        : days < 0 ? <span style={{ color: '#DC2626', fontSize: 12, fontWeight: 600 }}>{days}d overdue</span>
                        : days === 0 ? <span style={{ color: '#92400E', fontSize: 12, fontWeight: 600 }}>Today</span>
                        : <span style={{ color: 'var(--ink-3)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>{days}d</span>}
                    </Td>
                    <Td>
                      {r.dagrun_pm_linkedprojectid && r.dagrun_pm_linkedprojectname ? (
                        <span
                          style={{ fontSize: 12, color: 'var(--brand)', fontWeight: 500 }}
                          onClick={e => { e.stopPropagation(); }}
                        >
                          {r.dagrun_pm_linkedprojectname}
                        </span>
                      ) : '—'}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <NewRFPModal
          pca={pca}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); reload(); }}
        />
      )}
      {selected && (
        <RFPDetailDrawer
          pca={pca}
          rfp={selected}
          onClose={() => { setSelected(null); reload(); }}
          onChanged={() => reload()}
        />
      )}
    </div>
  );
}

function Th({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <th
      onClick={onClick}
      style={{
        padding: '10px 14px', fontSize: 11, fontWeight: 600,
        color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em',
        cursor: onClick ? 'pointer' : 'default', userSelect: 'none',
      }}
    >{children}</th>
  );
}
function Td({ children }: { children: React.ReactNode }) {
  return <td style={{ padding: '10px 14px', verticalAlign: 'middle' }}>{children}</td>;
}

// ── New RFP modal ─────────────────────────────────────────────────────

function NewRFPModal({ pca, onClose, onCreated }: {
  pca: IPublicClientApplication;
  onClose: () => void;
  onCreated: () => void;
}) {
  const me = useCurrentUser(pca, '');
  const [name, setName] = useState('');
  const [clientName, setClientName] = useState('');
  const [status, setStatus] = useState<number>(RFPStatus.Identified);
  const [estValue, setEstValue] = useState('');
  const [submissionDue, setSubmissionDue] = useState('');
  const [owner, setOwner] = useState<Person | null>(null);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (me && !owner) setOwner({ aadObjectId: me.oid, displayName: me.displayName });
  }, [me, owner]);

  const submit = async () => {
    if (!name.trim() || !clientName.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      const rfpId = await createRFP(pca, {
        name: name.trim(),
        clientname: clientName.trim(),
        status,
        estimatedvalue: estValue ? Number(estValue) : null,
        submissionduedate: submissionDue ? new Date(submissionDue).toISOString() : null,
        ownerid: owner?.aadObjectId ?? null,
        ownername: owner?.displayName ?? null,
        description: description.trim() || null,
      });
      // Activity log entry: created. Note: dagrun_pm_activitylog requires
      // a project FK. RFPs are tenant-level so we skip the call when no
      // project context exists — RFP-specific activity surfaces (the
      // detail panel's Activity section) read from a separate path.
      void rfpId;
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  const valid = name.trim().length > 0 && clientName.trim().length > 0;

  return (
    <BoardModal open title="New RFP" onClose={onClose} width={580}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !valid}>
          {saving ? 'Creating…' : 'Create RFP'}
        </BMPrimaryButton>
      </>}
    >
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>RFP Name *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)}
          style={BM_TITLE_INPUT} placeholder="e.g. Downtown Civic Center" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Client *</label>
        <input value={clientName} onChange={e => setClientName(e.target.value)}
          style={BM_INPUT} placeholder="Client / agency name" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Status</label>
          <select value={status} onChange={e => setStatus(Number(e.target.value))} style={BM_INPUT}>
            {PIPELINE_STATUSES.map(v => <option key={v} value={v}>{STATUS_LABEL[v]}</option>)}
          </select>
        </div>
        <div>
          <label style={BM_LABEL}>Estimated Value</label>
          <input type="number" value={estValue} onChange={e => setEstValue(e.target.value)}
            style={BM_INPUT} placeholder="0" />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Submission Due Date</label>
          <input type="date" value={submissionDue} onChange={e => setSubmissionDue(e.target.value)}
            style={BM_INPUT} />
        </div>
        <div>
          <label style={BM_LABEL}>Owner</label>
          <PeoplePicker pca={pca} value={owner} onChange={setOwner} placeholder="Owner…" />
        </div>
      </div>
      <div style={{ marginBottom: 4 }}>
        <label style={BM_LABEL}>Description / Scope</label>
        <AutoSizingTextarea value={description} onChange={setDescription}
          placeholder="Brief scope summary (optional)" />
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

// ── Detail drawer ─────────────────────────────────────────────────────

function RFPDetailDrawer({ pca, rfp, onClose, onChanged }: {
  pca: IPublicClientApplication;
  rfp: RFP;
  onClose: () => void;
  onChanged: () => void;
}) {
  const nav = useNavigate();
  const me = useCurrentUser(pca, '');
  const [draft, setDraft] = useState<RFP>(rfp);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showLinkBeacon, setShowLinkBeacon] = useState(false);
  const [beaconAvailable, setBeaconAvailable] = useState<boolean | null>(null);
  const [creatingDeal, setCreatingDeal] = useState(false);
  const [dealError, setDealError] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  const textDebounce = useRef<number | null>(null);

  useEffect(() => { setDraft(rfp); }, [rfp]);

  useEffect(() => {
    let cancelled = false;
    isBeaconInstalled(pca).then(v => { if (!cancelled) setBeaconAvailable(v); });
    return () => { cancelled = true; };
  }, [pca]);

  const patch = async (fields: Partial<RFP>) => {
    try {
      await updateRFP(pca, draft.dagrun_pm_rfpid, fields as Record<string, unknown>);
      setSavedFlash(true);
      setError(null);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const setAndPatch = <K extends keyof RFP>(k: K, v: RFP[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    patch({ [k]: v } as Partial<RFP>);
  };
  const setText = <K extends keyof RFP>(k: K, v: RFP[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    textDebounce.current = window.setTimeout(() => patch({ [k]: v } as Partial<RFP>), 600);
  };
  const flush = <K extends keyof RFP>(k: K, v: RFP[K]) => {
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    patch({ [k]: v } as Partial<RFP>);
  };

  const handleStatusChange = async (next: number) => {
    const prevStatus = draft.dagrun_pm_status;
    if (prevStatus === next) return;
    const fields: Partial<RFP> = { dagrun_pm_status: next };
    setDraft(d => ({ ...d, ...fields }));
    await patch(fields);
    // RFP activity log entry — written via dagrun_pm_activitylog when a
    // synthetic project context exists. RFPs sit outside any project,
    // so we use a special "tenant" project id of the empty GUID; the
    // activity feed for RFPs reads on the entity_id filter, not project.
    void logActivity(pca, {
      projectId: '00000000-0000-0000-0000-000000000000',
      entityType: ActivityEntityType.Project, // closest match — Project = tenant-level for our purposes
      entityId: draft.dagrun_pm_rfpid,
      entityTitle: draft.dagrun_pm_name,
      action: 'StatusChanged',
      description: `Status changed from ${STATUS_LABEL[prevStatus ?? 0] ?? '?'} to ${STATUS_LABEL[next]}`,
      actorId: me?.oid,
      actorName: me?.displayName,
    });
  };

  const doDelete = async () => {
    try {
      await deleteRFP(pca, draft.dagrun_pm_rfpid);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const showCreateProjectButton =
    draft.dagrun_pm_status === RFPStatus.Won && !draft.dagrun_pm_linkedprojectid;
  const showLinkedProjectLink =
    !!draft.dagrun_pm_linkedprojectid && !!draft.dagrun_pm_linkedprojectname;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 540,
        background: 'var(--surface)', borderLeft: '0.5px solid var(--border-light)',
        boxShadow: 'var(--shadow-panel)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{
            fontSize: 11,
            color: error ? 'var(--danger)' : 'var(--ink-4)',
            opacity: error || savedFlash ? 1 : 0,
            transition: 'opacity 0.2s',
          }}>{error ?? (savedFlash ? 'Saved ✓' : '')}</span>
          <button onClick={onClose} aria-label="Close" className="btn-ghost"
            style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>×</button>
        </div>

        <input value={draft.dagrun_pm_name}
          onChange={e => setText('dagrun_pm_name', e.target.value)}
          onBlur={e => flush('dagrun_pm_name', e.target.value)}
          style={{
            fontFamily: 'var(--font-ui)', fontSize: 22, fontWeight: 600,
            color: 'var(--ink)', border: 'none', padding: 0, marginBottom: 12,
            width: '100%', background: 'transparent',
          }} />

        {/* Status control */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <span style={pillStyle(draft.dagrun_pm_status)}>{STATUS_LABEL[draft.dagrun_pm_status ?? 0] ?? '—'}</span>
          <select
            value={draft.dagrun_pm_status ?? RFPStatus.Identified}
            onChange={e => handleStatusChange(Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}
          >
            {PIPELINE_STATUSES.map(v => <option key={v} value={v}>{STATUS_LABEL[v]}</option>)}
          </select>
        </div>

        {/* ── Overview section ── */}
        <Section title="Overview">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Client">
              <input value={draft.dagrun_pm_clientname ?? ''}
                onChange={e => setText('dagrun_pm_clientname', e.target.value || null)}
                onBlur={e => flush('dagrun_pm_clientname', e.target.value || null)}
                style={BM_INPUT}
                disabled={!!draft.dagrun_pm_crmdealid}
                title={draft.dagrun_pm_crmdealid ? 'Linked to Beacon — client name is sourced from the deal' : undefined} />
            </Field>
            <Field label="RFP Number">
              <input value={draft.dagrun_pm_rfpnumber ?? ''}
                onChange={e => setText('dagrun_pm_rfpnumber', e.target.value || null)}
                onBlur={e => flush('dagrun_pm_rfpnumber', e.target.value || null)}
                style={BM_INPUT} />
            </Field>
            <Field label="Estimated Value">
              <input
                type="number"
                value={draft.dagrun_pm_estimatedvalue ?? ''}
                onChange={e => setDraft(d => ({ ...d, dagrun_pm_estimatedvalue: e.target.value === '' ? null : Number(e.target.value) }))}
                onBlur={e => patch({ dagrun_pm_estimatedvalue: e.target.value === '' ? null : Number(e.target.value) })}
                style={BM_INPUT} />
            </Field>
            <Field label="Submission Due">
              <input type="date"
                value={draft.dagrun_pm_submissionduedate?.slice(0, 10) ?? ''}
                onChange={e => setAndPatch('dagrun_pm_submissionduedate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                style={BM_INPUT} />
            </Field>
            <div style={{ gridColumn: '1 / span 2' }}>
              <Field label="Owner">
                <PeoplePicker pca={pca}
                  value={draft.dagrun_pm_ownerid ? { aadObjectId: draft.dagrun_pm_ownerid, displayName: draft.dagrun_pm_ownername ?? draft.dagrun_pm_ownerid } : null}
                  onChange={p => {
                    const fields: Partial<RFP> = {
                      dagrun_pm_ownerid: p?.aadObjectId ?? null,
                      dagrun_pm_ownername: p?.displayName ?? null,
                    };
                    setDraft(d => ({ ...d, ...fields }));
                    patch(fields);
                  }}
                />
              </Field>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <Field label="Description / Scope">
              <AutoSizingTextarea
                value={draft.dagrun_pm_description ?? ''}
                onChange={v => setText('dagrun_pm_description', v || null)}
                onBlur={v => flush('dagrun_pm_description', v || null)}
              />
            </Field>
          </div>
          {draft.dagrun_pm_status === RFPStatus.GoNoBid && (
            <div style={{ marginTop: 12 }}>
              <Field label="Go/No-Bid Decision">
                <AutoSizingTextarea
                  value={draft.dagrun_pm_gonobiddecision ?? ''}
                  onChange={v => setText('dagrun_pm_gonobiddecision', v || null)}
                  onBlur={v => flush('dagrun_pm_gonobiddecision', v || null)}
                />
              </Field>
            </div>
          )}
        </Section>

        {/* ── Beacon section (only when installed) ── */}
        {beaconAvailable && (
          <Section title="Beacon Link">
            {draft.dagrun_pm_crmdealid ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 'var(--r-md)',
                background: 'var(--brand-mist)',
              }}>
                <span style={{ fontSize: 13, color: 'var(--brand-dark)', fontWeight: 600 }}>
                  Linked: {draft.dagrun_pm_clientname ?? draft.dagrun_pm_crmdealid.slice(0, 8)}
                </span>
                <div style={{ flex: 1 }} />
                <button
                  type="button"
                  onClick={() => {
                    const fields: Partial<RFP> = {
                      dagrun_pm_crmdealid: null,
                      dagrun_pm_crmaccountid: null,
                    };
                    setDraft(d => ({ ...d, ...fields }));
                    patch(fields);
                  }}
                  style={{
                    background: 'transparent', border: 'none',
                    color: 'var(--ink-3)', cursor: 'pointer',
                    fontSize: 14,
                  }}
                  title="Unlink"
                >×</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setShowLinkBeacon(true)}
                    style={{
                      background: 'transparent', color: 'var(--brand)',
                      border: '1px solid var(--brand)', borderRadius: 'var(--r-md)',
                      padding: '7px 12px',
                      fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >Link existing opportunity</button>
                  <button
                    type="button"
                    disabled={creatingDeal}
                    onClick={async () => {
                      setCreatingDeal(true); setDealError(null);
                      try {
                        const dealId = await createBeaconDeal(pca, {
                          name: draft.dagrun_pm_name,
                          value: draft.dagrun_pm_estimatedvalue ?? null,
                        });
                        const fields: Partial<RFP> = { dagrun_pm_crmdealid: dealId };
                        setDraft(d => ({ ...d, ...fields }));
                        patch(fields);
                      } catch (e) {
                        setDealError(e instanceof Error ? e.message : 'Could not create the opportunity.');
                      } finally { setCreatingDeal(false); }
                    }}
                    style={{
                      background: 'var(--brand)', color: '#fff',
                      border: 'none', borderRadius: 'var(--r-md)',
                      padding: '7px 12px',
                      fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600,
                      cursor: creatingDeal ? 'default' : 'pointer', opacity: creatingDeal ? 0.7 : 1,
                    }}
                  >{creatingDeal ? 'Creating…' : 'Create new opportunity'}</button>
                </div>
                {dealError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{dealError}</div>}
              </div>
            )}
          </Section>
        )}

        {/* ── Approvals ── */}
        {me && (
          <Section title="Approvals">
            <EntityApprovalSection
              pca={pca}
              projectId={null}
              entityType={ApprovalEntityType.RFP}
              entityId={draft.dagrun_pm_rfpid}
              entityTitle={draft.dagrun_pm_name}
              myOid={me.oid}
              canManage={me.isPM}
              activityEntityType={null}
            />
          </Section>
        )}

        {/* ── Project linking ── */}
        <Section title="Project">
          {showLinkedProjectLink && (
            <button
              type="button"
              onClick={() => nav(`/project/${draft.dagrun_pm_linkedprojectid}`)}
              style={{
                width: '100%', textAlign: 'left',
                padding: '10px 12px',
                background: 'var(--surface-2)',
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--r-md)',
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Project
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand)', marginTop: 2 }}>
                {draft.dagrun_pm_linkedprojectname} →
              </div>
            </button>
          )}
          {showCreateProjectButton && (
            <button
              type="button"
              onClick={() => setShowCreateProject(true)}
              className="btn-primary"
              style={{ width: '100%' }}
            >Create Project from this RFP</button>
          )}
          {!showCreateProjectButton && !showLinkedProjectLink && (
            <div style={{ fontSize: 12, color: 'var(--ink-4)', fontStyle: 'italic' }}>
              Mark the RFP as Won to enable project creation.
            </div>
          )}
        </Section>

        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
          <BMDangerButton onClick={() => setConfirmDelete(true)}>Delete RFP</BMDangerButton>
        </div>
      </div>

      {confirmDelete && (
        <BoardModal open title="Delete RFP" onClose={() => setConfirmDelete(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(false)} />
            <BMDangerButton onClick={doDelete}>Delete</BMDangerButton>
          </>}>
          Delete RFP <strong style={{ color: 'var(--ink)' }}>{draft.dagrun_pm_name}</strong>?
        </BoardModal>
      )}

      {showCreateProject && (
        <CreateProjectFromRFPModal
          pca={pca}
          rfp={draft}
          onClose={() => setShowCreateProject(false)}
          onCreated={(projectId, projectName) => {
            setShowCreateProject(false);
            const fields: Partial<RFP> = {
              dagrun_pm_linkedprojectid: projectId,
              dagrun_pm_linkedprojectname: projectName,
            };
            setDraft(d => ({ ...d, ...fields }));
            patch(fields);
            // Won RFP with a linked Beacon opportunity → advance the deal to
            // Won/Delivery in the Beacon environment. Best-effort + cross-env:
            // never blocks project navigation, silently no-ops if Beacon absent.
            if (draft.dagrun_pm_crmdealid) {
              markBeaconDealWon(pca, draft.dagrun_pm_crmdealid).catch(() => { /* non-fatal */ });
            }
            nav(`/project/${projectId}`);
          }}
        />
      )}

      {showLinkBeacon && beaconAvailable && (
        <LinkBeaconModal
          pca={pca}
          onClose={() => setShowLinkBeacon(false)}
          onLinked={(deal) => {
            setShowLinkBeacon(false);
            const accountId = deal._dagrun_accountid_value ?? null;
            const accountName = deal['_dagrun_accountid_value@OData.Community.Display.V1.FormattedValue']
              ?? draft.dagrun_pm_clientname ?? null;
            const fields: Partial<RFP> = {
              dagrun_pm_crmdealid: deal.dagrun_dealid,
              dagrun_pm_crmaccountid: accountId,
              dagrun_pm_clientname: accountName,
            };
            setDraft(d => ({ ...d, ...fields }));
            patch(fields);
          }}
        />
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div className="eyebrow" style={{ marginBottom: 10 }}>{title.toUpperCase()}</div>
      {children}
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={BM_LABEL}>{label}</label>
      {children}
    </div>
  );
}

// ── Create Project from Won RFP ───────────────────────────────────────

function CreateProjectFromRFPModal({ pca, rfp, onClose, onCreated }: {
  pca: IPublicClientApplication;
  rfp: RFP;
  onClose: () => void;
  onCreated: (projectId: string, projectName: string) => void;
}) {
  const [projectName, setProjectName] = useState(rfp.dagrun_pm_name);
  const [clientName, setClientName] = useState(rfp.dagrun_pm_clientname ?? '');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!projectName.trim() || creating) return;
    setCreating(true); setError(null);
    try {
      const id = await createProject(pca, {
        name: projectName.trim(),
        description: rfp.dagrun_pm_description ?? null,
        modules: { milestones: true, agile: true, issues: true, risks: true },
      });
      onCreated(id, projectName.trim());
      void clientName; // client name carried on RFP record; project schema has no client field
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setCreating(false); }
  };

  return (
    <BoardModal open title="Create Project from RFP" onClose={onClose} width={500}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={creating} />
        <BMPrimaryButton onClick={submit} disabled={creating || !projectName.trim()}>
          {creating ? 'Creating…' : 'Create Project'}
        </BMPrimaryButton>
      </>}>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 14 }}>
        This creates a new project linked back to this RFP. You'll land on the project's overview after creation.
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Project Name *</label>
        <input autoFocus value={projectName} onChange={e => setProjectName(e.target.value)}
          style={BM_TITLE_INPUT} />
      </div>
      <div style={{ marginBottom: 4 }}>
        <label style={BM_LABEL}>Client</label>
        <input value={clientName} onChange={e => setClientName(e.target.value)}
          style={BM_INPUT} />
        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>
          Stored on the RFP record. Project records reference the client through the linked RFP.
        </div>
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

// ── Beacon link modal ─────────────────────────────────────────────────

function LinkBeaconModal({ pca, onClose, onLinked }: {
  pca: IPublicClientApplication;
  onClose: () => void;
  onLinked: (deal: BeaconDeal) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<BeaconDeal[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Initial fetch + debounced re-search.
  useEffect(() => {
    let cancelled = false;
    setSearching(true); setError(null);
    const t = window.setTimeout(async () => {
      try {
        const rows = await searchBeaconDeals(pca, query);
        if (!cancelled) setResults(rows);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Search failed');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [pca, query]);

  const selected = results.find(r => r.dagrun_dealid === selectedId) ?? null;

  return (
    <BoardModal open title="Link to Beacon Opportunity" onClose={onClose} width={620}
      footer={<>
        <BMCancelButton onClick={onClose} />
        <BMPrimaryButton onClick={() => selected && onLinked(selected)} disabled={!selected}>
          Link
        </BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 12 }}>
        <input
          autoFocus
          placeholder="Search Beacon deals by name…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={BM_INPUT}
        />
      </div>
      <div style={{
        border: '1px solid var(--border-light)',
        borderRadius: 'var(--r-md)',
        maxHeight: 320, overflowY: 'auto',
      }}>
        {searching && <div style={{ padding: 16, fontSize: 12, color: 'var(--ink-4)' }}>Searching…</div>}
        {!searching && results.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--ink-4)' }}>
            {error ?? 'No deals found.'}
          </div>
        )}
        {!searching && results.map(d => {
          const isSelected = d.dagrun_dealid === selectedId;
          return (
            <div
              key={d.dagrun_dealid}
              onClick={() => setSelectedId(d.dagrun_dealid)}
              style={{
                padding: '10px 12px',
                borderBottom: '0.5px solid var(--border-light)',
                cursor: 'pointer',
                background: isSelected ? 'var(--brand-mist)' : 'transparent',
                transition: 'background 0.12s',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{d.dagrun_name}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                {d['_dagrun_accountid_value@OData.Community.Display.V1.FormattedValue'] ?? '—'}
                {d.dagrun_value != null && <span style={{ marginLeft: 12, fontFamily: 'var(--font-mono)' }}>${d.dagrun_value.toLocaleString()}</span>}
              </div>
            </div>
          );
        })}
      </div>
      {error && !searching && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}
